import { useEffect, useState } from "react";
import api from "../lib/api";
import { Wrench, Camera } from "lucide-react";
import Lightbox from "./Lightbox";

export default function RestorationTimeline({ computerId }) {
    const [entries, setEntries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [lb, setLb] = useState(null); // {images, index}

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get(`/computers/${computerId}/restorations`);
                setEntries(data);
            } catch {
                setEntries([]);
            } finally {
                setLoading(false);
            }
        })();
    }, [computerId]);

    if (loading) return null;
    if (entries.length === 0) return null;

    const openLb = (images, idx) => setLb({ images, index: idx });

    return (
        <div data-testid="restoration-timeline" className="mt-12 border border-border">
            <div className="border-b border-border bg-muted/40 px-6 py-3 flex items-center justify-between">
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-phosphor-amber flex items-center gap-2">
                    <Wrench className="h-3 w-3" />
                    &gt; RESTORATION_LOG.HIST
                </div>
                <div className="font-mono text-[10px] text-muted-foreground">
                    {entries.length} {entries.length === 1 ? "entry" : "entries"}
                </div>
            </div>
            <ol className="relative">
                {entries.map((e, idx) => {
                    const before = e.before_images || [];
                    const after = e.after_images || [];
                    return (
                        <li
                            key={e.id}
                            data-testid={`restoration-entry-${e.id}`}
                            className="grid grid-cols-12 gap-4 border-b border-border/60 px-6 py-6 last:border-b-0"
                        >
                            <div className="col-span-12 md:col-span-3">
                                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-phosphor-green">
                                    ● {e.date}
                                </div>
                                {e.author && (
                                    <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                                        by {e.author}
                                    </div>
                                )}
                                {typeof e.cost === "number" && (
                                    <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-phosphor-amber">
                                        ${e.cost.toLocaleString()}
                                    </div>
                                )}
                                {(before.length > 0 || after.length > 0) && (
                                    <div className="mt-2 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                                        <Camera className="h-3 w-3" />
                                        {before.length + after.length} photos
                                    </div>
                                )}
                            </div>
                            <div className="col-span-12 md:col-span-9">
                                <div className="font-serif text-xl text-foreground">
                                    {String(idx + 1).padStart(2, "0")}. {e.title}
                                </div>
                                {e.notes && (
                                    <p className="mt-2 font-sans text-sm text-muted-foreground leading-relaxed whitespace-pre-wrap">
                                        {e.notes}
                                    </p>
                                )}

                                {(before.length > 0 || after.length > 0) && (
                                    <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid={`restoration-photos-${e.id}`}>
                                        {before.length > 0 && (
                                            <PhotoStrip
                                                label="Before"
                                                testid={`restoration-before-${e.id}`}
                                                images={before}
                                                onClick={(i) => openLb(before, i)}
                                                accent="text-muted-foreground"
                                            />
                                        )}
                                        {after.length > 0 && (
                                            <PhotoStrip
                                                label="After"
                                                testid={`restoration-after-${e.id}`}
                                                images={after}
                                                onClick={(i) => openLb(after, i)}
                                                accent="text-phosphor-green"
                                            />
                                        )}
                                    </div>
                                )}
                            </div>
                        </li>
                    );
                })}
            </ol>

            {lb && (
                <Lightbox
                    images={lb.images}
                    index={lb.index}
                    onClose={() => setLb(null)}
                    onChange={(i) => setLb({ ...lb, index: i })}
                />
            )}
        </div>
    );
}

function PhotoStrip({ label, testid, images, onClick, accent }) {
    return (
        <div data-testid={testid} className="border border-border bg-card">
            <div className={`flex items-center justify-between border-b border-border px-3 py-2 font-mono text-[10px] uppercase tracking-[0.25em] ${accent}`}>
                <span>{label}</span>
                <span className="text-muted-foreground">{images.length}</span>
            </div>
            <div className="grid grid-cols-3 gap-1 p-2">
                {images.map((src, i) => (
                    <button
                        key={`${src}-${i}`}
                        type="button"
                        data-testid={`${testid}-thumb-${i}`}
                        onClick={() => onClick(i)}
                        className="scanlines border border-border hover:border-phosphor-amber transition-colors"
                    >
                        <img src={src} alt="" className="w-full h-20 object-cover" />
                    </button>
                ))}
            </div>
        </div>
    );
}
